import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Button,Paper,Skeleton,Table,TableCell,TableRow,Typography } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import { createTheme,ThemeProvider } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import MUIDataTable,{ MUIDataTableMeta,MUIDataTableOptions } from "mui-datatables";
import { useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Users from '../../service/Users';
import { tableOptions } from '../TableHelpers/TableOptions';
import ChangeUserModal from './Modal/ChangeUserModal';

export type userBackObj = {
  uidLogin?: string,
  aliasname: string,
  contato: string,
  dataAlteracao: number,
  dataCadastro: number,
  email: string,
  empresa: string,
  foto: string,
  gerente: string,
  id: string,
  matricula: string,
  nome: string,
  notificacao: string,
  passwdReset: string,
  perfil: string,
  status: string,
  aprovador: boolean,
  listaEquipesNomes: string[],
  tags?: [],
  listaEquipes: string[],
  gestorPA?: boolean,
  inspetorPA?: boolean,
  dataInativacao?: number
};

type userResponse = {
  users: userBackObj[],
};

const usersService = new Users();

export default function ListaUsuarios() {
  const [userId, setUserId] = useState<string>('');
  const { t } = useTranslation('translation');
  const [openModal, setOpenModal] = useState(false);

  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: ['GET_USERS'],
    queryFn: async () => {
      toast.info(t('gestaoUsuarios.buscaUsuario'), { toastId: 'fetching-users' });
      const token = await AttToken();
      if (token) {
        const body = { ...token };
        const result: userResponse = await usersService.getAllUsers(body);
        return result.users;
      }
    },
    retry: 2,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const onLoading = isLoading || isFetching;

  useEffect(() => {
    if (isError) {
      trackEventMatomo('gestão de usuários', 'erro', 'carregamento', 'falha ao carregar usuários');
      toast.error(t('gestaoUsuarios.erroBuscaUsuarios'), { toastId: 'fetch-users-error' });
    }
    if (isSuccess && !data?.length) {
      toast.warning(t('gestaoUsuarios.semUsuarios'), { toastId: 'fetch-users-empty' });
    }

    if (isSuccess && data?.length) {
      trackEventMatomo('gestão de usuários', 'sucesso', 'carregamento', 'usuários carregados');
      toast.success(t('gestaoUsuarios.usuariosRetornados'), { toastId: 'fetch-users-success' });
    }
  }, [isSuccess, isError, onLoading, data, t]);

  const columns = [
    {
      name: "nome",
      label: t('name'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "email",
      label: t('email'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "contato",
      label: t('tel'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "matricula",
      label: t('registration'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "perfil",
      label: t('profile'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => {
          if (value === 'Usuario') {
            return t('profiles.user');
          } else return value;
        }

        ,
      }
    },
    {
      name: "listaEquipesNomes",
      label: t("team"),
      options: {
        viewColumns: false,
        hidden: true,
        filter: false,
        sort: false,
        sortThirdClickReset: false,
        display: false
      }
    },
    {
      name: "status",
      label: t('status'),
      options: {
        customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
          const dataInativacao = data?.[tableMeta.rowIndex]?.dataInativacao;
          if (value === 'ativo') {
            return <Typography textAlign='center' sx={{ color: '#00C4A7' }} fontWeight={600}>{`${t('active')}`}</Typography>;
          } if (value === 'inativo') {
            return <Typography textAlign='center' color='error' fontWeight={600}>
              {`${t('inactive')} ${t('gestaoUsuarios.em')} ${dataInativacao ? new Date(dataInativacao).toLocaleDateString('pt-BR') : t('gestaoUsuarios.semData')}`}
            </Typography>;
          }
        },
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              variant='outlined'
              endIcon={<BorderColorIcon />}
              className='button'
              onClick={() => {
                setUserId(value);
                trackEventMatomo('pesquisa de usuário', 'click', 'button', 'abre modal de edição');
                setOpenModal(true);
              }}>
              {t('edit')}
            </Button>
          );
        },
        filter: false,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    viewColumns: true,
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: false,
    selectableRowsHeader: false,
    isRowExpandable: () => {
      return true;
    },
    renderExpandableRow: (_rowData, rowMeta) => {
      const originalData = data?.[rowMeta.dataIndex];
      const equipes = originalData?.listaEquipesNomes;

      if (!equipes || equipes.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={_rowData.length}>
              <Typography variant="body2" color="textSecondary" align="center">
                {t('gestaoUsuarios.semEquipes')}
              </Typography>
            </TableCell>
          </TableRow>
        );
      }
      return (
        <TableRow sx={{ padding: 2, margin: 2 }}>
          <TableCell colSpan={6}>
            <Paper sx={{ width: '100%', padding: 2, margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {`${t('team')}(s)`}
              </Typography>
              <Table size="small" >
                <TableBody>
                  {equipes.map((team: string, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{team}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </TableCell>
        </TableRow>
      );
    },
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: (column) =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      }
    }
  };

  const getMuiTheme = () => createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          elevation2: {
            width: 'fit-content',
          },
          elevation8: {
            width: 'fit-content',
          },
          root: {
            width: '100%',
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '5px'
          },
        },
      },
    }
  });

  return (
    <>
      {openModal && (
        <ChangeUserModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          userId={userId} />
      )}
      {onLoading
        ? <Skeleton variant='rounded' width='100%' height={400} />
        : <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={t('users.userSearch.userList')}
            data={data ?? []}
            columns={columns}
            options={{ ...tableOptions, ...translatedTextLabels }}
          />
        </ThemeProvider>}
    </>
  );
} 